import React from "react";
import {
  Box,
  Container,
  Skeleton,
  Card,
  CardContent,
  Grid,
} from "@mui/material";

/**
 * Enhanced skeleton loader for all detail screens
 * Matches the actual content structure for realistic loading state
 */
export const DetailSkeleton: React.FC = () => {
  return (
    <Box sx={{ minHeight: "100vh", bgcolor: "#f5f7fa", pb: 3 }}>
      {/* Hero Skeleton */}
      <Box
        sx={{
          position: "relative",
          height: { xs: 400, md: 500 },
          overflow: "hidden",
        }}
      >
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ borderRadius: 0 }}
        />

        {/* Back Button Skeleton */}
        <Skeleton
          variant="circular"
          width={48}
          height={48}
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            bgcolor: "rgba(255,255,255,0.9)",
          }}
        />

        {/* Category Badge Skeleton */}
        <Skeleton
          variant="rectangular"
          width={120}
          height={32}
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            borderRadius: 4,
            bgcolor: "rgba(255,255,255,0.9)",
          }}
        />

        {/* Hero Content Skeleton - Bottom */}
        <Box
          sx={{
            position: "absolute",
            bottom: 80,
            left: 0,
            right: 0,
            px: { xs: 2, md: 3 },
          }}
        >
          <Container maxWidth="lg">
            {/* Status Badge Skeleton */}
            <Skeleton
              variant="rectangular"
              width={120}
              height={36}
              sx={{ borderRadius: 3, mb: 2, bgcolor: "rgba(255,255,255,0.2)" }}
            />

            {/* Title Skeleton */}
            <Skeleton
              variant="text"
              width="80%"
              height={60}
              sx={{ mb: 2, bgcolor: "rgba(255,255,255,0.2)" }}
            />

            {/* Meta Info Skeletons */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap", mb: 1 }}>
              <Skeleton
                variant="rectangular"
                width={200}
                height={40}
                sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.2)" }}
              />
              <Skeleton
                variant="rectangular"
                width={180}
                height={40}
                sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.2)" }}
              />
            </Box>

            {/* Views & Shares Skeleton */}
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <Skeleton
                variant="rectangular"
                width={120}
                height={40}
                sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.2)" }}
              />
              <Skeleton
                variant="rectangular"
                width={140}
                height={40}
                sx={{ borderRadius: 2, bgcolor: "rgba(255,255,255,0.2)" }}
              />
            </Box>
          </Container>
        </Box>

        {/* Image Navigation Dots Skeleton */}
        <Box
          sx={{
            position: "absolute",
            bottom: 16,
            left: "50%",
            transform: "translateX(-50%)",
            display: "flex",
            gap: 1,
          }}
        >
          {[1, 2, 3].map((i) => (
            <Skeleton
              key={i}
              variant="circular"
              width={8}
              height={8}
              sx={{ bgcolor: "rgba(255,255,255,0.5)" }}
            />
          ))}
        </Box>
      </Box>

      {/* Main Content - Slides up over hero */}
      <Box
        sx={{
          position: "relative",
          zIndex: 10,
          background: "linear-gradient(180deg, #f5f7fa 0%, #ffffff 100%)",
          borderTopLeftRadius: { xs: 24, md: 32 },
          borderTopRightRadius: { xs: 24, md: 32 },
          boxShadow: "0 -8px 32px rgba(0,0,0,0.12)",
          mt: -3,
        }}
      >
        <Container maxWidth="lg" sx={{ pt: 4, pb: 2 }}>
          {/* Description Card Skeleton */}
          <Card
            elevation={0}
            sx={{
              mb: 3,
              borderRadius: 3,
              border: "1px solid",
              borderColor: "divider",
            }}
          >
            <CardContent sx={{ p: { xs: 2, md: 3 } }}>
              <Skeleton variant="text" width="40%" height={32} sx={{ mb: 2 }} />
              <Skeleton
                variant="text"
                width="100%"
                height={20}
                sx={{ mb: 1 }}
              />
              <Skeleton variant="text" width="95%" height={20} sx={{ mb: 1 }} />
              <Skeleton variant="text" width="85%" height={20} />
            </CardContent>
          </Card>

          {/* Grid Content Skeleton */}
          <Grid container spacing={3}>
            {/* Info Cards - 2 columns on desktop */}
            {[1, 2].map((i) => (
              <Grid item xs={12} md={6} key={i}>
                <Card
                  elevation={0}
                  sx={{
                    height: "100%",
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    {/* Card Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width={48}
                        height={48}
                        sx={{ borderRadius: 2 }}
                      />
                      <Skeleton variant="text" width={140} height={28} />
                    </Box>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={1}
                      sx={{ mb: 2 }}
                    />
                    {/* Card Content */}
                    <Box
                      sx={{ display: "flex", flexDirection: "column", gap: 2 }}
                    >
                      <Box>
                        <Skeleton
                          variant="text"
                          width="30%"
                          height={16}
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton variant="text" width="60%" height={24} />
                      </Box>
                      <Box>
                        <Skeleton
                          variant="text"
                          width="30%"
                          height={16}
                          sx={{ mb: 0.5 }}
                        />
                        <Skeleton variant="text" width="70%" height={24} />
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}

            {/* Engagement Stats Card Skeleton - Full width */}
            <Grid item xs={12}>
              <Card
                elevation={0}
                sx={{
                  borderRadius: 3,
                  background:
                    "linear-gradient(135deg, #1a237e 0%, #3949ab 100%)",
                  position: "relative",
                  overflow: "hidden",
                }}
              >
                <Box
                  sx={{
                    position: "relative",
                    zIndex: 1,
                    display: "grid",
                    gridTemplateColumns: { xs: "1fr 1fr", sm: "1fr 1fr" },
                    gap: 3,
                    p: 3,
                  }}
                >
                  {/* Views Skeleton */}
                  <Box sx={{ textAlign: "center" }}>
                    <Skeleton
                      variant="circular"
                      width={32}
                      height={32}
                      sx={{
                        mx: "auto",
                        mb: 1,
                        bgcolor: "rgba(255,255,255,0.2)",
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width={80}
                      height={36}
                      sx={{
                        mx: "auto",
                        mb: 0.5,
                        bgcolor: "rgba(255,255,255,0.2)",
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width={60}
                      height={20}
                      sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.2)" }}
                    />
                  </Box>
                  {/* Shares Skeleton */}
                  <Box sx={{ textAlign: "center" }}>
                    <Skeleton
                      variant="circular"
                      width={32}
                      height={32}
                      sx={{
                        mx: "auto",
                        mb: 1,
                        bgcolor: "rgba(255,255,255,0.2)",
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width={80}
                      height={36}
                      sx={{
                        mx: "auto",
                        mb: 0.5,
                        bgcolor: "rgba(255,255,255,0.2)",
                      }}
                    />
                    <Skeleton
                      variant="text"
                      width={60}
                      height={20}
                      sx={{ mx: "auto", bgcolor: "rgba(255,255,255,0.2)" }}
                    />
                  </Box>
                </Box>
              </Card>
            </Grid>

            {/* Additional Content Cards Skeleton */}
            {[1, 2].map((i) => (
              <Grid item xs={12} key={`extra-${i}`}>
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  <CardContent sx={{ p: { xs: 2, md: 3 } }}>
                    {/* Card Header */}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                        mb: 2,
                      }}
                    >
                      <Skeleton
                        variant="rectangular"
                        width={48}
                        height={48}
                        sx={{ borderRadius: 2 }}
                      />
                      <Skeleton variant="text" width={180} height={28} />
                    </Box>
                    <Skeleton
                      variant="rectangular"
                      width="100%"
                      height={1}
                      sx={{ mb: 2 }}
                    />
                    {/* Chips Skeleton */}
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                      {[1, 2, 3, 4, 5].map((j) => (
                        <Skeleton
                          key={j}
                          variant="rectangular"
                          width={100}
                          height={32}
                          sx={{ borderRadius: 2 }}
                        />
                      ))}
                    </Box>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </Container>
      </Box>

      {/* Bottom Share Bar Skeleton */}
      <Box
        sx={{
          position: "fixed",
          bottom: 0,
          left: 0,
          right: 0,
          zIndex: 1200,
          bgcolor: "white",
          boxShadow: "0 -4px 20px rgba(0,0,0,0.15)",
          pb: { xs: 2, sm: 0 },
        }}
      >
        <Container maxWidth="lg">
          <Box
            sx={{
              py: { xs: 1.5, sm: 2 },
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 2,
            }}
          >
            {/* Info Skeleton - Hidden on mobile */}
            <Box
              sx={{
                display: { xs: "none", sm: "flex" },
                gap: 1.5,
                alignItems: "center",
              }}
            >
              <Skeleton
                variant="rectangular"
                width={48}
                height={48}
                sx={{ borderRadius: 2 }}
              />
              <Box>
                <Skeleton
                  variant="text"
                  width={200}
                  height={20}
                  sx={{ mb: 0.5 }}
                />
                <Skeleton variant="text" width={150} height={16} />
              </Box>
            </Box>
            {/* Buttons Skeleton */}
            <Box
              sx={{
                display: "flex",
                gap: 1.5,
                width: { xs: "100%", sm: "auto" },
              }}
            >
              <Skeleton
                variant="rectangular"
                width={180}
                height={48}
                sx={{ borderRadius: 2, flex: { xs: 1, sm: 0 } }}
              />
              <Skeleton
                variant="rectangular"
                width={180}
                height={48}
                sx={{ borderRadius: 2, flex: { xs: 1, sm: 0 } }}
              />
            </Box>
          </Box>
        </Container>
      </Box>
    </Box>
  );
};
